import { Formik, Form, useFormikContext } from 'formik'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import * as Yup from 'yup'
import { _, withCapture } from 'utils'
import FullPageLoading from 'components/FullPageLoading'
import Button from 'components/Button'
import constants from 'constants/constants'
import SelectField from 'features/forms/components/SelectField'
import SubmitButton from 'features/forms/components/SubmitButton'
import { PosterItemFragment } from 'generated/graphql'
import TextField from '../../forms/components/TextField'
import { showNotification } from '../../notifications'
import { showErrorNotification } from '../../notifications/notificationsSlice'
import Switch from '../../forms/components/Switch'
import FileUpload, { IMAGE_ONLY } from '../../forms/components/FileUpload'
import { createPoster } from '../graphql/mutation'
import Editor from '../../forms/components/Editor'

const S = {
    Form: styled(Form)`
    `,
    Switch: styled(Switch)`
        margin-top: 5px;
        margin-bottom: 40px !important;
    `,
    Actions: styled.div`
        display: flex;
        justify-content: flex-end;
        width: 100%;
        margin-top: 24px;
        padding-right: 10px;
    `
}

interface Props {
    close?: () => void
    callGetPosters?: () => void
    minOrder?: number,
    spaceId: number
    enableChat: boolean
}

export const FormContent = ({ close, enableChat }: { close?: () => void, enableChat: boolean }) => {
    const { initialValues } = useFormikContext<PosterItemFragment>()
    const hasLoggedOn = initialValues?.author?.hasLoggedOn

    return (
        <S.Form className="row">
            <TextField
                name="title"
                label={_('poster.form.title')}
                isRequired
            />
            <Editor
                name="abstract"
                label={_('poster.form.abstract')}
            />
            <FileUpload
                name="preview"
                label={_('poster.form.preview')}
                accept={IMAGE_ONLY}
                placeholder={_('poster.form.preview')}
                hideLibrary
            />
            <FileUpload
                name="pdf"
                label={_('poster.form.pdf')}
                accept="application/pdf"
                placeholder={_('poster.form.pdf')}
                isRequired
            />
            <TextField
                name="authorNames"
                label={_('poster.form.authorNames')}
            />
            <TextField
                name="videoUrl"
                label={_('poster.form.videoUrl')}
            />
            <TextField
                name="authorEmail"
                label={_('poster.form.presenter_email')}
                isActiveStatus={hasLoggedOn}
                status={!initialValues?.author ? '' : (hasLoggedOn
                    ? _('poster.form.author_logged_status')
                    : _('poster.form.author_pending_status'))}
                statusHelperText={hasLoggedOn
                    ? _('poster.form.author_logged_helper_text')
                    : _('poster.form.author_pending_helper_text')}
            />
            <SelectField
                name="locationType"
                label={_('attendee.form.location_type')}
                options={constants.getLocationOptions()}
            />
            <Switch
                name="enableChat"
                label={_('poster.form.enable_chat')}
                isDisabled={!enableChat}
                helperText={!enableChat && _('poster.form.enable_chat_disable_by_parent')}
            />
            <S.Actions>
                <Button onClick={close} variant="secondary">
                    {_('button.cancel')}
                </Button>
                <SubmitButton />
            </S.Actions>
        </S.Form>
    )
}

export const PosterValidation = Yup.object().shape({
    title: Yup.string().required(),
    pdf: Yup.string().required()
})

const CreatePoster = ({
    close, callGetPosters, spaceId, enableChat
}: Props) => {
    const dispatch = useDispatch()
    const [isUpLoading, setIsUploading] = useState(false)

    const onSubmit = withCapture(async (values) => {
        setIsUploading(true)
        const response = await createPoster({ ...values, spaceId })
        setIsUploading(false)

        if (!response.data || response.errors.length) {
            dispatch(showErrorNotification(response.errors))
            return
        }

        dispatch(showNotification({
            type: 'success',
            body: _('form.notification.create_success'),
        }))
        callGetPosters()
        close()
    })

    return (
        <Formik
            initialValues={{ title: '', pdf: '' }}
            validationSchema={PosterValidation}
            onSubmit={onSubmit}
        >
            <>
                <FormContent close={close} enableChat={enableChat} />
                {isUpLoading && <FullPageLoading isAbsolute fullWidth />}
            </>
        </Formik>
    )
}

export default CreatePoster
